import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { User, Mail, Lock, Phone, Eye, EyeOff, ArrowLeft, Loader2, ChevronRight } from "lucide-react";

/**
 * End-client account creation. Role="client" auto-activates.
 *
 * Route: /cliente/criar-conta (also /register?role=client redirects here).
 */
export default function ClientSignup() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("A palavra-passe precisa de pelo menos 8 caracteres.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        name: form.name.trim(),
        role: "client",
      });
      await refresh();
      toast.success("Conta criada ✓");
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white" data-testid="client-signup-page">
      <div className="px-6 py-10 max-w-md mx-auto">
        <Link to="/app" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#f59e0b]">
          <ArrowLeft className="w-3.5 h-3.5" /> Voltar
        </Link>

        <div className="mt-8 mb-8">
          <div className="text-3xl font-display tracking-wide">
            DI<span style={{ color: "#f59e0b" }}>TOURS</span>
          </div>
          <h1 className="font-display text-3xl mt-6">Criar conta</h1>
          <p className="text-sm opacity-70 mt-2">Guarde as suas reservas, veja o histórico e receba notificações da sua viagem em tempo real.</p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="client-signup-form">
          <Field label="Nome completo" icon={User} testId="client-signup-name">
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full h-12 bg-white/5 border border-white/20 px-3 text-white focus:border-[#f59e0b] outline-none"
              placeholder="João Silva"
            />
          </Field>

          <Field label="Email" icon={Mail} testId="client-signup-email">
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full h-12 bg-white/5 border border-white/20 px-3 text-white focus:border-[#f59e0b] outline-none"
              placeholder="voce@exemplo.pt"
            />
          </Field>

          <Field label="Telemóvel (opcional)" icon={Phone} testId="client-signup-phone">
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full h-12 bg-white/5 border border-white/20 px-3 text-white focus:border-[#f59e0b] outline-none"
              placeholder="+351 …"
            />
          </Field>

          <Field label="Palavra-passe (mín. 8 caracteres)" icon={Lock} testId="client-signup-password">
            <div className="relative">
              <input
                required
                minLength={8}
                type={showPwd ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full h-12 bg-white/5 border border-white/20 px-3 pr-11 text-white focus:border-[#f59e0b] outline-none"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                data-testid="client-signup-password-toggle"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-white/60 hover:text-[#f59e0b]"
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </Field>

          <button
            type="submit"
            disabled={loading}
            data-testid="client-signup-submit"
            className="w-full h-12 mt-2 bg-[#f59e0b] text-[#0a0f1e] font-bold uppercase text-xs tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Criar conta <ChevronRight className="w-4 h-4" /></>}
          </button>

          <p className="text-[11px] text-white/50 text-center leading-relaxed">
            Ao criar conta aceita os nossos{" "}
            <Link to="/termos" className="text-[#f59e0b] underline">Termos</Link> e a{" "}
            <Link to="/privacidade" className="text-[#f59e0b] underline">Política de Privacidade</Link>.
          </p>
        </form>

        <div className="mt-8 pt-6 border-t border-white/10 text-center">
          <div className="text-xs text-white/50">Já tem conta?</div>
          <Link to="/login" className="mt-2 inline-block text-sm text-[#f59e0b] uppercase tracking-widest font-bold" data-testid="client-signup-login-link">
            Entrar →
          </Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, icon: Icon, testId, children }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#f59e0b] mb-1.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      {children}
    </label>
  );
}
